import moment from "moment";
import { normalizeCopilotDegradedPayload } from "./copilotDegradedMode";

const HAS_TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse Copilot timestamps into browser-local moment instances.
 *
 * Live messages use ISO strings with ``Z``. Laravel history returns UTC values
 * without an offset (e.g. ``2026-07-15 05:36:00``) — those must be interpreted
 * as UTC before converting to the user's local timezone.
 *
 * @param {string} [iso]
 * @returns {import("moment").Moment | null}
 */
export const parseChatTimestamp = (iso) => {
  if (!iso) return null;
  const value = String(iso).trim();
  if (!value) return null;

  const date = HAS_TIMEZONE_SUFFIX.test(value)
    ? moment(value)
    : moment.utc(value).local();

  return date.isValid() ? date : null;
};

/**
 * @param {string} [iso]
 * @returns {string}
 */
export const formatChatTimestamp = (iso) => {
  const date = parseChatTimestamp(iso);
  if (!date) return "";

  const now = moment();
  if (date.isSame(now, "day")) {
    return `Today • ${date.format("h:mm A")}`;
  }
  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday • ${date.format("h:mm A")}`;
  }
  return `${date.format("MMM D, YYYY")} • ${date.format("h:mm A")}`;
};

/**
 * Compact timestamp for inside chat bubbles (WhatsApp-style).
 * @param {string} [iso]
 * @returns {string}
 */
export const formatBubbleTimestamp = (iso) => {
  const date = parseChatTimestamp(iso);
  if (!date) return "";

  const now = moment();
  if (date.isSame(now, "day")) {
    return date.format("h:mm a");
  }
  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday, ${date.format("h:mm a")}`;
  }
  return `${date.format("MMM D")}, ${date.format("h:mm a")}`;
};

/**
 * @param {import('../constants/vertex360CopilotConstants').Vertex360CopilotMessage[]} messages
 * @param {number} index
 * @returns {boolean}
 */
export const shouldShowTimestamp = (messages, index) => {
  if (!messages?.length || index < 0 || index >= messages.length) return false;
  const current = parseChatTimestamp(messages[index].createdAt);
  if (!current) return true;

  if (index === 0) return true;

  const previous = parseChatTimestamp(messages[index - 1].createdAt);
  if (!previous) return true;

  const sameDay = current.isSame(previous, "day");
  const withinFiveMinutes =
    Math.abs(current.diff(previous, "minutes")) <= 5;

  return !sameDay || !withinFiveMinutes;
};

/**
 * @returns {string}
 */
export const createMessageId = () =>
  `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * @param {import('../constants/vertex360CopilotConstants').Vertex360CopilotMessageRole} role
 * @param {string} content
 * @param {string} [createdAt]
 * @returns {import('../constants/vertex360CopilotConstants').Vertex360CopilotMessage}
 */
export const createMessage = (role, content, createdAt) => ({
  id: createMessageId(),
  role,
  content: String(content || "").trim(),
  createdAt: createdAt || new Date().toISOString(),
});

const PERSON_OPTION_KINDS = new Set([
  "pt",
  "ep",
  "mg",
  "mgr",
  "esp",
  "ahesp",
  "participant",
  "participants",
  "worker",
  "employee",
  "employee_profiles",
  "manager",
  "admin",
  "users",
  "external_sp",
  "external_provider",
  "external_service_providers",
  "ah_external_service_providers",
]);

const NORMALIZE_PERSON_KIND = {
  pt: "pt",
  participant: "pt",
  participants: "pt",
  ep: "ep",
  worker: "ep",
  employee: "ep",
  employee_profiles: "ep",
  mg: "mg",
  mgr: "mg",
  manager: "mg",
  admin: "mg",
  users: "mg",
  esp: "esp",
  external_sp: "esp",
  external_provider: "esp",
  external_service_providers: "esp",
  ahesp: "ahesp",
  ah_external_service_providers: "ahesp",
};

/**
 * Opaque FE↔BE person chip ids (`pt:`, `ep:`, `mg:`, `esp:`) — structured parse.
 *
 * @param {string} [value]
 * @returns {{ kind: "pt"|"ep"|"mg"|"esp"|"ahesp"|string, id: number } | null}
 */
export const parsePersonOptionId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  let body = raw;
  const lower = body.toLowerCase();
  if (lower.startsWith("person_")) {
    body = body.slice("person_".length);
  }
  const sep = body.includes(":") ? ":" : body.includes("_") ? "_" : "";
  if (!sep) return null;
  const idx = body.indexOf(sep);
  const kind = body.slice(0, idx).toLowerCase();
  const idPart = body.slice(idx + 1);
  if (!PERSON_OPTION_KINDS.has(kind)) return null;
  if (!idPart || String(Number(idPart)) !== idPart) return null;
  const id = Number(idPart);
  if (!Number.isFinite(id) || id <= 0) return null;
  return { kind: NORMALIZE_PERSON_KIND[kind] || kind, id };
};

const isPersonOptionId = (value) => parsePersonOptionId(value) != null;

/**
 * Build the follow-up message sent when a user picks a clarify option.
 *
 * Strips any previous "Clarification: ..." line(s) from the base question so
 * repeated disambiguations never stack up (which bloated the prompt and
 * confused the model), then appends exactly one clarification line.
 *
 * Opaque person chips (`pt:123` / `ep:456`) bind via `clarify_selected_option`
 * on the API payload. The Clarification line is UI-facing — prefer the human
 * label so the chat card never shows raw `pt:` / `ep:` ids.
 *
 * @param {string} [basePrompt] the originating question (may already contain a clarification)
 * @param {string} [choice] the selected option id/label (e.g. "participant")
 * @param {string} [displayLabel] optional human label when `choice` is an opaque id
 * @returns {string}
 */
export const buildClarifyFollowUp = (basePrompt, choice, displayLabel) => {
  const cleaned = String(basePrompt || "")
    .split("\n")
    .filter((line) => !/^\s*clarification\s*:/i.test(line))
    .join("\n")
    .trim();
  const label = String(choice || "").trim();
  const pretty = String(displayLabel || "").trim();
  if (!cleaned) return pretty || label;
  if (!label && !pretty) return cleaned;
  // Opaque person pick — show the chip label in chat; id travels on the API.
  if (isPersonOptionId(label)) {
    const shown = pretty && !isPersonOptionId(pretty) ? pretty : label;
    return `${cleaned}\nClarification: selected person ${shown}.`;
  }
  // Dual-person role pairings use the option id/label as-is so the SQL lane
  // can bind worker vs participant for BOTH names.
  if (
    /p1_worker_p2_participant|p1_participant_p2_worker|both_workers|both_participants/i.test(
      label
    ) ||
    (/=\s*worker/i.test(label) && /=\s*participant/i.test(label)) ||
    /both\s+are\s+(workers|participants)/i.test(label)
  ) {
    return `${cleaned}\nClarification: ${label}.`;
  }
  // AU state/territory chips must not be rewritten as person names.
  if (
    /^(nsw|vic|qld|sa|wa|tas|nt|act|new south wales|victoria|queensland|south australia|western australia|tasmania|northern territory|australian capital territory)$/i.test(
      label
    )
  ) {
    return `${cleaned}\nClarification: state=${label}.`;
  }
  // Free-text alternate name from "Type a different name".
  if (
    /^[A-Za-z][A-Za-z\s\-']{1,80}$/.test(label) &&
    !/^(participant|worker|other_role|retype_name|type a different name)$/i.test(
      label
    )
  ) {
    return `${cleaned}\nClarification: person name is ${label}.`;
  }
  return `${cleaned}\nClarification: the person is ${label}.`;
};

/**
 * Split a user message that embeds ``Clarification: …`` into display parts.
 * Wire text still keeps the Clarification line for the model; the UI can show
 * a cleaner card instead of raw ``region=…; state=…`` text.
 *
 * @param {string} [content]
 * @returns {{ base: string, clarifications: Array<{ label: string, value: string }> }}
 */
export const parseClarifyUserContent = (content) => {
  const raw = String(content || "");
  const lines = raw.split("\n");
  const baseLines = [];
  const clarLines = [];
  for (const line of lines) {
    if (/^\s*clarification\s*:/i.test(line)) {
      clarLines.push(line.replace(/^\s*clarification\s*:\s*/i, "").trim());
    } else {
      baseLines.push(line);
    }
  }
  const base = baseLines.join("\n").trim();
  const joined = clarLines.join("; ").replace(/\.\s*$/, "");
  const clarifications = [];
  if (joined) {
    // Split on ; while keeping key=value pairs
    for (const part of joined.split(/;\s*/)) {
      const piece = part.replace(/\.$/, "").trim();
      if (!piece) continue;
      const eq = piece.indexOf("=");
      if (eq > 0) {
        const rawKey = piece.slice(0, eq).trim();
        const rawVal = piece.slice(eq + 1).trim();
        // Legacy: `ep:3170=Name` — never show the opaque id as the row label.
        if (isPersonOptionId(rawKey)) {
          clarifications.push({
            label: "Person",
            value: isPersonOptionId(rawVal) ? "Person" : rawVal,
          });
        } else {
          const label = rawKey
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          clarifications.push({
            label,
            value: rawVal,
          });
        }
      } else if (/^the person is\s+/i.test(piece)) {
        clarifications.push({
          label: "Role",
          value: piece.replace(/^the person is\s+/i, "").trim(),
        });
      } else if (/^selected person\s+/i.test(piece)) {
        const rawPerson = piece.replace(/^selected person\s+/i, "").trim();
        // Legacy messages may still contain opaque pt:/ep: ids — never surface
        // those as the only clarification value when a friendlier form exists.
        const opaque = parsePersonOptionId(rawPerson);
        if (opaque) {
          const roleByKind = {
            ep: "Worker",
            pt: "Participant",
            mg: "Manager",
            esp: "External provider",
            ahesp: "External provider",
          };
          clarifications.push({
            label: "Person",
            value: roleByKind[opaque.kind] || "Person",
          });
        } else {
          clarifications.push({
            label: "Person",
            value: rawPerson,
          });
        }
      } else if (/^person name is\s+/i.test(piece)) {
        clarifications.push({
          label: "Name",
          value: piece.replace(/^person name is\s+/i, "").trim(),
        });
      } else if (/^service provider scope is\s+/i.test(piece)) {
        clarifications.push({
          label: "Service provider",
          value: piece.replace(/^service provider scope is\s+/i, "").trim(),
        });
      } else {
        clarifications.push({ label: "Clarification", value: piece });
      }
    }
  }
  return { base, clarifications };
};

/**
 * Infer Clarification key when LLM omits ``field`` on geo qa prompts.
 *
 * @param {object} [question]
 * @returns {string}
 */
const inferClarifyField = (question) => {
  const explicit = String(question?.field || "").trim();
  if (explicit) return explicit;
  const text = String(question?.question || "")
    .trim()
    .toLowerCase();
  if (!text) return "";
  const hasRegion = /\bregions?\b/.test(text);
  const hasState = /\bstates?\b/.test(text);
  if (/\bservice\s+support\b/.test(text)) return "service_type";
  if (/\baudit\b|opt[\s-]?in|opt[\s-]?out/.test(text)) return "opt_in_status";
  if (/\bndis\b/.test(text)) return "ndis_no";
  if (/\bfirst\s*name\b/.test(text)) return "first_name";
  if (/\blast\s*name\b/.test(text)) return "last_name";
  if (/\bemail\b/.test(text)) return "email";
  if (/\b(?:phone|mobile|contact\s+number)\b/.test(text)) return "phone";
  if (/\b(?:assigned\s+worker|worker)\b/.test(text)) return "assigned_worker";
  if (/\bcreated\b/.test(text)) return "created_at";
  if (hasRegion && !hasState) return "region";
  if (hasState && !hasRegion) return "state";
  if (/\bwhich\s+region\b|^\s*region\b/.test(text)) return "region";
  if (/\bwhich\s+state\b|^\s*state\b/.test(text)) return "state";
  if (/\bstatus\b/.test(text)) return "status";
  return "";
};

const isUsableClarifyId = (value) => {
  const id = String(value ?? "").trim();
  return Boolean(id) && id !== "undefined" && id !== "null";
};

/**
 * Stable question id for typed clarify answers. Never returns the literal
 * ``"undefined"`` (JS object-key coercion when ``question.id`` is missing).
 *
 * @param {object} [question]
 * @param {number} [index]
 * @returns {string}
 */
export const resolveClarifyQuestionId = (question, index = 0) => {
  if (isUsableClarifyId(question?.id)) return String(question.id).trim();
  const field = String(question?.field || "").trim();
  if (field) return field;
  const inferred = inferClarifyField(question);
  if (inferred) return inferred;
  const ctype = String(
    question?.clarify_type || question?.clarifyType || ""
  ).trim();
  if (ctype && !["mcq", "qa", "filter_enum"].includes(ctype)) return ctype;
  const safeIndex = Number.isFinite(Number(index)) ? Number(index) : 0;
  return `q${safeIndex + 1}`;
};

/**
 * Ensure every clarify question has a usable ``id`` (field / inferred / qN).
 *
 * @param {Array} [questions]
 * @returns {Array}
 */
export const normalizeClarifyQuestions = (questions) => {
  if (!Array.isArray(questions)) return [];
  return questions.map((q, index) => {
    if (!q || typeof q !== "object") return q;
    return { ...q, id: resolveClarifyQuestionId(q, index) };
  });
};

/**
 * Remap answer keys onto resolved question ids. Collapses broken keys like
 * ``"undefined"`` onto the matching question instead of sending them upstream.
 *
 * @param {Record<string, object>|null|undefined} answers
 * @param {Array} [questions]
 * @returns {Record<string, object>}
 */
export const sanitizeClarifyAnswers = (answers, questions) => {
  const list = normalizeClarifyQuestions(questions);
  const raw =
    answers && typeof answers === "object" && !Array.isArray(answers)
      ? answers
      : {};
  const out = {};
  const usedRawKeys = new Set();

  list.forEach((q, index) => {
    const qid = resolveClarifyQuestionId(q, index);
    const candidates = [
      qid,
      q?.id,
      q?.field,
      inferClarifyField(q),
      `q${index + 1}`,
      String(index),
    ]
      .map((k) => String(k ?? "").trim())
      .filter(isUsableClarifyId);

    let hitKey = candidates.find((k) => raw[k] != null);
    if (!hitKey && list.length === 1 && raw.undefined != null) {
      hitKey = "undefined";
    }
    if (!hitKey && list.length === 1 && raw.null != null) {
      hitKey = "null";
    }
    if (hitKey != null && raw[hitKey] != null) {
      out[qid] = raw[hitKey];
      usedRawKeys.add(hitKey);
    }
  });

  // Multi-question: assign leftover broken keys in order to unanswered qs.
  const brokenKeys = Object.keys(raw).filter(
    (k) => !usedRawKeys.has(k) && (!isUsableClarifyId(k) || k === "undefined")
  );
  let brokenIdx = 0;
  list.forEach((q, index) => {
    const qid = resolveClarifyQuestionId(q, index);
    if (out[qid] != null || brokenIdx >= brokenKeys.length) return;
    out[qid] = raw[brokenKeys[brokenIdx]];
    brokenIdx += 1;
  });

  return out;
};

/**
 * Resolve a chip's human label from the question options list.
 * Opaque ids like ``region:25`` must never be shown as the display value when
 * the catalog label is available.
 *
 * @param {object} [question]
 * @param {string} [optionId]
 * @param {string} [fallbackText]
 * @returns {string}
 */
export const resolveClarifyOptionLabel = (
  question,
  optionId,
  fallbackText = ""
) => {
  const opaqueCatalogId = (value) => {
    const s = String(value || "").trim().toLowerCase();
    if (!s.includes(":")) return false;
    const [kind, rest] = s.split(":");
    if (!rest || String(Number(rest)) !== rest) return false;
    return kind === "region" || kind === "service_type";
  };
  const text = String(fallbackText || "").trim();
  if (text && !opaqueCatalogId(text) && !isPersonOptionId(text)) return text;
  const oid = String(optionId || "").trim();
  if (!oid) return text;
  const options = Array.isArray(question?.options) ? question.options : [];
  const match = options.find(
    (opt) => String(opt?.id ?? "").trim() === oid
  );
  const label = String(match?.label || match?.name || "").trim();
  if (label) return label;
  // Never surface opaque catalog / person ids in the clarifications card.
  if (opaqueCatalogId(oid) || opaqueCatalogId(text) || isPersonOptionId(oid)) {
    return "";
  }
  return text || oid;
};

/**
 * Chip option id for ``clarify_selected_option``. Prefers a real option_id
 * over ``__typed__`` (reserved for free-text-only confirms).
 *
 * @param {Array} [questions]
 * @param {Record<string, object>} [answers]
 * @returns {string|null}
 */
export const pickClarifySelectedOption = (questions, answers) => {
  const list = normalizeClarifyQuestions(questions);
  const map = sanitizeClarifyAnswers(answers, list);
  let chipId = null;
  for (let index = 0; index < list.length; index += 1) {
    const q = list[index];
    const qid = resolveClarifyQuestionId(q, index);
    const ans = map[qid];
    if (!ans || typeof ans !== "object") continue;
    const oid = String(ans.option_id || ans.optionId || "").trim();
    if (!oid || oid === "__typed__" || oid === "__abandoned__") continue;
    chipId = oid;
    const field = inferClarifyField(q);
    // Prefer filter chips (state/status/etc.) when several option_ids exist.
    if (
      field === "state" ||
      field === "region" ||
      field === "service_type" ||
      field === "opt_in_status" ||
      field === "projection" ||
      field === "status"
    ) {
      return oid;
    }
  }
  return chipId;
};

/**
 * Build follow-up text from typed clarify v1 answers map.
 *
 * @param {string} [basePrompt]
 * @param {Array} [questions]
 * @param {Record<string, object>} [answers]
 * @returns {string}
 */
export const buildClarifyFollowUpFromAnswers = (
  basePrompt,
  questions,
  answers
) => {
  const cleaned = String(basePrompt || "")
    .split("\n")
    .filter((line) => !/^\s*clarification\s*:/i.test(line))
    .join("\n")
    .trim();
  const bits = [];
  const list = normalizeClarifyQuestions(questions);
  const map = sanitizeClarifyAnswers(answers, list);

  list.forEach((q, index) => {
    const qid = resolveClarifyQuestionId(q, index);
    const ans = map[qid];
    if (!ans) return;
    const ctype = String(q?.clarify_type || q?.clarifyType || "mcq");
    const field = inferClarifyField(q);

    if (
      ctype === "person_role" ||
      ctype === "mcq" ||
      ctype === "filter_enum" ||
      ctype === "service_provider" ||
      ctype === "dual_person_role" ||
      ctype === "true_false"
    ) {
      const oid = String(ans.option_id || ans.optionId || ans.id || "").trim();
      const text = String(ans.text || ans.selected_label || "").trim();
      const display = resolveClarifyOptionLabel(q, oid, text);
      if (ctype === "person_role") {
        bits.push(`the person is ${display || oid}`);
      } else if (ctype === "dual_person_role") {
        bits.push(oid || display);
      } else if (ctype === "service_provider") {
        bits.push(`service provider scope is ${oid}`);
      } else if (ctype === "true_false") {
        const yes =
          ans.bool === true || oid === "true" || oid === "yes" || oid === "1";
        bits.push(`${field || "confirm"}=${yes ? "yes" : "no"}`);
      } else if (isPersonOptionId(oid)) {
        // Identity disambiguation chips (pt:/ep:) — never use the opaque id
        // as the clarification key (that rendered as "Ep:3170 | Name").
        const shown =
          display && !isPersonOptionId(display) ? display : "selected person";
        bits.push(`selected person ${shown}`);
      } else if (display || oid) {
        // Never use opaque ids as field keys.
        const key = field && !isPersonOptionId(field) ? field : "choice";
        bits.push(`${key}=${display || oid}`);
      }
    } else if (ctype === "qa") {
      const text = String(ans.text || "").trim();
      if (text) bits.push(`${field || "answer"}=${text}`);
    } else if (ctype === "date_range") {
      bits.push(`date_from=${ans.from || ""}; date_to=${ans.to || ""}`);
    }
  });

  if (!bits.length) return cleaned;
  const suffix = bits.join("; ");
  if (!cleaned) return `Clarification: ${suffix}.`;
  return `${cleaned}\nClarification: ${suffix}.`;
};

const normalizeFeedbackRating = (value) => {
  const cleaned = String(value || "").toLowerCase().trim();
  return cleaned === "up" || cleaned === "down" ? cleaned : undefined;
};

/** Map a Laravel message resource to the local chat shape. */
export const mapServerCopilotMessage = (message) => {
  const feedbackRating = normalizeFeedbackRating(message.feedback_rating);
  let render = message.render || undefined;
  if (render?.type === "clarify") {
    render = normalizeClarifyStreamRender({ render }) || render;
  }
  const degraded = normalizeCopilotDegradedPayload(message);
  return {
    id: String(message.id),
    role: message.role,
    content: String(message.content || ""),
    createdAt: message.created_at,
    render,
    sources:
      Array.isArray(message.sources) && message.sources.length
        ? message.sources
        : undefined,
    ...(feedbackRating ? { feedbackRating } : {}),
    ...(degraded.isReducedMode
      ? {
          isReducedMode: true,
          degradeReason: degraded.degradeReason,
          servicePath: degraded.servicePath,
        }
      : {}),
  };
};

/**
 * Normalize mid-stream clarify SSE (`type: "clarify"`) into the render shape
 * ChatMessage expects: `{ type: "clarify", options: [...] }`.
 *
 * @param {object} payload
 * @returns {{ type: "clarify", options: Array }|null}
 */
export const normalizeClarifyStreamRender = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const fromRender =
    payload.render?.type === "clarify" ? payload.render : null;
  const rawQuestions = Array.isArray(fromRender?.questions)
    ? fromRender.questions
    : Array.isArray(payload.questions)
      ? payload.questions
      : null;
  const questions = rawQuestions?.length
    ? normalizeClarifyQuestions(rawQuestions)
    : null;
  const options = Array.isArray(fromRender?.options)
    ? fromRender.options
    : Array.isArray(payload.options)
      ? payload.options
      : null;
  if ((!options || !options.length) && (!questions || !questions.length)) {
    return null;
  }
  const rawAnswers = fromRender?.answers;
  const answers =
    rawAnswers && typeof rawAnswers === "object" && questions?.length
      ? sanitizeClarifyAnswers(rawAnswers, questions)
      : rawAnswers;
  return {
    type: "clarify",
    version: fromRender?.version || payload.version || (questions ? 1 : undefined),
    ...(questions?.length ? { questions } : {}),
    ...(options?.length ? { options } : {}),
    ...(answers ? { answers } : {}),
    ...(fromRender?.selected ? { selected: fromRender.selected } : {}),
    ...(fromRender?.selected_label
      ? { selected_label: fromRender.selected_label }
      : {}),
  };
};
